/**
 * Google Places API (New) helpers for place enrichment (photos, rating, reviews).
 * Uses import.meta.env.VITE_GOOGLE_MAPS_API_KEY only — never hardcode keys.
 * Do not log the API key.
 */

const PLACES_BASE = "https://places.googleapis.com/v1";
const DEFAULT_MAX_WIDTH_PX = 800;
const PHOTO_STRIP_MAX = 6;
const REVIEW_MAX = 5;

export type PlaceReview = {
  authorName: string;
  rating?: number;
  text: string;
  relativeTime?: string;
};

export type PlacePhotoResult = {
  /** Resource name: places/{placeId}/photos/{photoRef} — primary / first photo */
  photoName: string;
  /** Additional photo resource names (incl. primary as [0]) */
  photoNames: string[];
  /** Media URL including maxWidthPx + key (for <img src>). */
  mediaUrl: string;
  /** Media URLs for photo strip (same order as photoNames). */
  mediaUrls: string[];
  credit: string;
  placeId?: string;
  rating?: number;
  userRatingCount?: number;
  reviews?: PlaceReview[];
};

export type PlaceLookupInput = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  mapsQuery?: string;
  placeId?: string;
};

/** Safe read of Vite client env (works in Node tests where import.meta.env may be missing). */
export function getGoogleMapsApiKey(): string | undefined {
  try {
    const env = import.meta.env as ImportMetaEnv | undefined;
    const raw = env?.VITE_GOOGLE_MAPS_API_KEY;
    if (typeof raw !== "string") return undefined;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  } catch {
    return undefined;
  }
}

export function hasGoogleMapsApiKey(): boolean {
  return Boolean(getGoogleMapsApiKey());
}

/** Build Places photo media URL (photo refs expire; always prefer this endpoint). */
export function placePhotoMediaUrl(
  photoName: string,
  maxWidthPx: number = DEFAULT_MAX_WIDTH_PX,
  apiKey: string = getGoogleMapsApiKey() ?? "",
): string {
  const name = photoName.replace(/^\/+/, "").replace(/\/media$/, "");
  const params = new URLSearchParams({
    maxWidthPx: String(maxWidthPx),
    key: apiKey,
  });
  return `${PLACES_BASE}/${name}/media?${params.toString()}`;
}

function attributionCredit(
  attributions: Array<{ displayName?: string }> | undefined,
): string {
  const names = (attributions ?? [])
    .map((a) => a.displayName?.trim())
    .filter(Boolean);
  if (names.length === 0) return "Google Maps";
  return `Google · ${names.slice(0, 2).join(", ")}`;
}

type PhotosField = Array<{
  name?: string;
  authorAttributions?: Array<{ displayName?: string }>;
}>;

type ReviewsField = Array<{
  authorAttribution?: { displayName?: string };
  rating?: number;
  text?: { text?: string };
  relativePublishTimeDescription?: string;
}>;

type PlacePayload = {
  id?: string;
  photos?: PhotosField;
  rating?: number;
  userRatingCount?: number;
  reviews?: ReviewsField;
};

function mapReviews(reviews: ReviewsField | undefined): PlaceReview[] | undefined {
  if (!reviews?.length) return undefined;
  const mapped: PlaceReview[] = [];
  for (const r of reviews) {
    const text = r.text?.text?.trim() ?? "";
    if (!text) continue;
    mapped.push({
      authorName: r.authorAttribution?.displayName?.trim() || "Google user",
      rating: typeof r.rating === "number" ? r.rating : undefined,
      text,
      relativeTime: r.relativePublishTimeDescription,
    });
    if (mapped.length >= REVIEW_MAX) break;
  }
  return mapped.length > 0 ? mapped : undefined;
}

function resultFromPlace(
  place: PlacePayload,
  placeId?: string,
): PlacePhotoResult | null {
  const key = getGoogleMapsApiKey();
  if (!key) return null;
  const photos = (place.photos ?? []).filter((p) => p.name).slice(0, PHOTO_STRIP_MAX);
  if (photos.length === 0) return null;
  const photoNames = photos.map((p) => p.name!);
  const first = photos[0];
  const rating =
    typeof place.rating === "number" && Number.isFinite(place.rating)
      ? place.rating
      : undefined;
  const userRatingCount =
    typeof place.userRatingCount === "number" &&
    Number.isFinite(place.userRatingCount)
      ? place.userRatingCount
      : undefined;
  return {
    photoName: photoNames[0],
    photoNames,
    mediaUrl: placePhotoMediaUrl(photoNames[0], DEFAULT_MAX_WIDTH_PX, key),
    mediaUrls: photoNames.map((n) =>
      placePhotoMediaUrl(n, DEFAULT_MAX_WIDTH_PX, key),
    ),
    credit: attributionCredit(first.authorAttributions),
    placeId: place.id ?? placeId,
    rating,
    userRatingCount,
    reviews: mapReviews(place.reviews),
  };
}

const DETAILS_FIELD_MASK =
  "id,photos,rating,userRatingCount,reviews";
const SEARCH_FIELD_MASK =
  "places.id,places.photos,places.rating,places.userRatingCount,places.reviews";

/** Place Details (New) when we already have a Google place id. */
export async function fetchPlacePhotoByPlaceId(
  placeId: string,
  signal?: AbortSignal,
): Promise<PlacePhotoResult | null> {
  const key = getGoogleMapsApiKey();
  if (!key || !placeId.trim()) return null;

  const id = placeId.trim().replace(/^places\//, "");
  const res = await fetch(`${PLACES_BASE}/places/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: {
      "X-Goog-Api-Key": key,
      "X-Goog-FieldMask": DETAILS_FIELD_MASK,
    },
    signal,
  });
  if (!res.ok) return null;
  const data = (await res.json()) as PlacePayload;
  return resultFromPlace(data, data.id ?? id);
}

/**
 * Text Search (New) biased to pin lat/lng.
 * Field mask includes photos + rating + reviews (single lookup path).
 */
export async function fetchPlacePhotoByText(
  input: PlaceLookupInput,
  signal?: AbortSignal,
): Promise<PlacePhotoResult | null> {
  const key = getGoogleMapsApiKey();
  if (!key) return null;

  const textQuery = (input.mapsQuery ?? input.name).trim();
  if (!textQuery) return null;
  if (!Number.isFinite(input.lat) || !Number.isFinite(input.lng)) return null;

  const body = {
    textQuery,
    maxResultCount: 1,
    locationBias: {
      circle: {
        center: { latitude: input.lat, longitude: input.lng },
        radius: 2000.0,
      },
    },
  };

  const res = await fetch(`${PLACES_BASE}/places:searchText`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": key,
      "X-Goog-FieldMask": SEARCH_FIELD_MASK,
    },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { places?: PlacePayload[] };
  const place = data.places?.[0];
  if (!place) return null;
  return resultFromPlace(place, place.id);
}

/** Resolve a pin to Places enrichment (placeId first, then Text Search). */
export async function resolvePlacePhoto(
  input: PlaceLookupInput,
  signal?: AbortSignal,
): Promise<PlacePhotoResult | null> {
  if (!hasGoogleMapsApiKey()) return null;
  if (input.placeId?.trim()) {
    const byId = await fetchPlacePhotoByPlaceId(input.placeId, signal);
    if (byId) return byId;
  }
  return fetchPlacePhotoByText(input, signal);
}

/** True when a pin can attempt Places photo resolution. */
export function pinEligibleForPlacesPhoto(
  pin: Pick<PlaceLookupInput, "mapsQuery" | "name" | "lat" | "lng" | "placeId">,
): boolean {
  if (!hasGoogleMapsApiKey()) return false;
  if (!Number.isFinite(pin.lat) || !Number.isFinite(pin.lng)) return false;
  return Boolean(
    pin.placeId?.trim() || pin.mapsQuery?.trim() || pin.name?.trim(),
  );
}

/** Show PlacePhoto when Commons URL exists or Places can resolve. */
export function pinMayHavePhoto(
  pin?: Pick<
    PlaceLookupInput & { imageUrl?: string },
    "imageUrl" | "mapsQuery" | "name" | "lat" | "lng" | "placeId"
  > | null,
): boolean {
  if (!pin) return false;
  if (pin.imageUrl) return true;
  return pinEligibleForPlacesPhoto(pin);
}
